import React, { useState } from 'react';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'motion/react';
import { CloudIcon, ShellIcon, StarIcon } from './ModeIcons';
import CurrencyIcon from './CurrencyIcon';
import FeatureIcon from './FeatureIcon';
import Prism3D from './Prism3D';
import ChestIcon from './ChestIcon';
import { Clock, Star, Target, ArrowLeft, Trophy, Gift, Zap, Package, Award } from 'lucide-react';
import { useMissions, UserMission } from '../hooks/useMissions';
import { UserData } from '../hooks/useUser';
import { MISSIONS, MissionDefinition } from '../utils/missions';
import MissionCard from './MissionCard';
import { cn } from '../lib/utils';
import { audioEngine } from '../utils/audioEngine';
import AnimaTicketIcon from './AnimaTicketIcon';
import GameModal, { ModalType } from './GameModal';
import PremiumModal from './PremiumModal';
import { useTranslation } from 'react-i18next';
const traitColorMap: Record<string, string> = {
  red: "#f43f5e", yellow: "#fbbf24", green: "#10b981", blue: "#38bdf8", violet: "#a855f7"
};

const potentialColorMap: Record<string, string> = {
  gc_multi: "#fbbf24",
  gc_bonus: "#fbbf24",
  exp_bonus: "#10b981",
  reward_multi: "#f97316",
  hybrid_bonus: "#f97316",
  reward_flat: "#6366f1",
  score_bonus: "#a855f7",
  fever_bonus: "#f43f5e",
  time_bonus: "#38bdf8"
};

interface MissionsProps {
  user: UserData | null;
  onBack: () => void;
  addExp: (amt: number, delayQueue?: boolean) => Promise<any>;
  addCoins: (amt: number) => Promise<void>;
  addGems: (amt: number) => Promise<void>;
  addTickets: (amt: number) => Promise<void>;
  addBadge: (badgeId: string) => Promise<void>;
  onClaimDailyGift: (double: boolean) => Promise<any>;
  addRewardBox: (tier: 'bronze' | 'silver' | 'gold' | 'diamond') => Promise<void>;
  onQueueLevelUp: (result: any) => void;
  onSetPremiumStatus?: (status: boolean) => Promise<void>;
}

type Tab = 'daily' | 'weekly' | 'monthly' | 'lifetime';

export default function Missions({ user, onBack, addExp, addCoins, addGems, addTickets, addBadge, onClaimDailyGift, addRewardBox, onQueueLevelUp, onSetPremiumStatus }: MissionsProps) {
  const { t } = useTranslation();
  const [pendingLevelUps, setPendingLevelUps] = useState<any[]>([]);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const wrappedAddExp = async (amt: number) => {
    const res = await addExp(amt, true);
    if (res?.leveledUp || (res?.levelUps && res.levelUps.length > 0)) {
      setPendingLevelUps(prev => [...prev, res]);
    }
    return res;
  };

  const { missions, loading, claimReward, refresh, claimableCounts } = useMissions(user, { addExp: wrappedAddExp, addCoins, addGems, addTickets, addBadge, addRewardBox });
  const [activeTab, setActiveTab] = useState<Tab>('daily');
  const [obtainedRewards, setObtainedRewards] = useState<any[]>([]);
  const [claimedReward, setClaimedReward] = useState<MissionDefinition | null>(null);

  const handleClaim = async (userMissionId: string) => {
    const um = missions.find(m => m.id === userMissionId);
    if (!um) return false;
    const def = MISSIONS.find(d => d.id === um.missionId);
    
    const result = await claimReward(userMissionId);
    if (result && Array.isArray(result) && def) {
      audioEngine.playClaim();
      setObtainedRewards(result);
      setClaimedReward(def);
    }
    return !!result;
  };

  const filteredMissions = missions.filter(m => {
    const def = MISSIONS.find(d => d.id === m.missionId);
    if (!def || def.type !== activeTab) return false;

    const now = new Date();
    if (def.type === 'daily' && m.expiresAt !== format(now, 'yyyy-MM-dd')) return false;
    if (def.type === 'weekly' && m.expiresAt !== format(now, "yyyy-'W'II")) return false;
    if (def.type === 'monthly' && m.expiresAt !== format(now, 'yyyy-MM')) return false;

    // Progressive lifetime filtering: Only show current active tier
    if (def.type === 'lifetime') {
      const parts = def.id.split('.');
      if (parts.length === 2) {
          const tierNum = parseInt(parts[1]);
          if (tierNum > 1) {
              const prevId = `${parts[0]}.${tierNum - 1}`;
              const prevUm = missions.find(um => um.missionId === prevId);
              if (!prevUm?.isClaimed) return false;
          }
          // If claimed, only show if it's the last one? 
          // Let's hide claimed lifetime missions if the next tier is already being tracked/shown
          if (m.isClaimed && tierNum < 4) {
             return false; 
          }
      }
    }

    return true;
  }).sort((a, b) => {
      // 1. Claimed status (Claimed always goes to bottom)
      if (a.isClaimed && !b.isClaimed) return 1;
      if (!a.isClaimed && b.isClaimed) return -1;

      // 2. Both are not claimed - check if ready to claim (progress >= goal)
      if (!a.isClaimed && !b.isClaimed) {
        const defA = MISSIONS.find(d => d.id === a.missionId);
        const defB = MISSIONS.find(d => d.id === b.missionId);
        const isReadyA = a.progress >= (defA?.goal || Infinity);
        const isReadyB = b.progress >= (defB?.goal || Infinity);

        if (isReadyA && !isReadyB) return -1;
        if (!isReadyA && isReadyB) return 1;
      }
      
      // 3. Same status, maintain original definition order
      const indexA = MISSIONS.findIndex(d => d.id === a.missionId);
      const indexB = MISSIONS.findIndex(d => d.id === b.missionId);
      return indexA - indexB;
  });

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: 'daily', label: t('missions.tabs.daily'), icon: Clock },
    { id: 'weekly', label: t('missions.tabs.weekly'), icon: Trophy },
    { id: 'monthly', label: t('missions.tabs.monthly'), icon: Target },
    { id: 'lifetime', label: t('missions.tabs.lifetime'), icon: Star },
  ];

  return (
    <div className="flex flex-col h-full bg-[#f8fafc] text-slate-900 relative overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-6 py-6 flex flex-col gap-4 relative z-10 shadow-sm">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
                <button 
                  onClick={() => {
                    audioEngine.playSound('click');
                    onBack();
                  }}
                  className="p-3 rounded-2xl bg-slate-50 border-2 border-slate-100 hover:bg-white transition-all active:translate-y-1 shadow-sm"
                >
                  <ArrowLeft className="w-6 h-6 text-slate-400" />
                </button>

                <div className="flex flex-col">
                    <h2 className="text-3xl font-num font-black uppercase tracking-tight leading-none text-slate-900">{t('missions.title')}</h2>
                    <p className="text-[10px] text-cyan-600 font-black uppercase tracking-[0.2em] mt-1">
                        {t('missions.tab_counter', {
                          tab: t(`missions.tabs.${activeTab}`),
                          active: filteredMissions.filter(m => !m.isClaimed).length,
                          total: filteredMissions.length
                        })}
                    </p>
                </div>
            </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-slate-100 p-1 rounded-2xl">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  audioEngine.playSound('click');
                  setActiveTab(tab.id);
                }}
                className={cn(
                  "flex-1 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all relative",
                  isActive ? "bg-white text-cyan-600 shadow-sm" : "text-slate-400 hover:text-slate-600"
                )}
              >

                {tab.label}
                {claimableCounts && claimableCounts[tab.id] > 0 && (
                  <div className="absolute -top-1.5 -right-1 min-w-[16px] h-[16px] px-1 bg-rose-500 border border-white rounded-full shadow-sm flex items-center justify-center">
                    <span className="text-[9px] font-black text-white">{claimableCounts[tab.id]}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Mission List */}
      <div className="flex-1 overflow-y-auto px-6 pb-24 relative z-10 scrollbar-none">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-cyan-500 rounded-full animate-spin" />
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('missions.loading')}</p>
          </div>
        ) : (
          <div className="py-4 space-y-4">
            {filteredMissions.length > 0 ? (
                <AnimatePresence mode='popLayout'>
                  {filteredMissions.map((um) => {
                    const def = MISSIONS.find(d => d.id === um.missionId);
                    if (!def) return null;
                    return (
                      <motion.div key={um.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}>
                        <MissionCard 
                          missionDef={def as MissionDefinition} 
                          userMission={um as UserMission} 
                          onClaim={handleClaim} 
                          isPremium={user?.isPremium || false}
                          onLockClick={() => setShowPremiumModal(true)}
                        />
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center px-8">
                <div className="w-20 h-20 bg-slate-50 rounded-[32px] flex items-center justify-center mb-6 border-2 border-dashed border-slate-200">
                    <Gift className="w-10 h-10 text-slate-200" />
                </div>
                <h3 className="text-2xl font-num uppercase text-slate-300">{t('missions.all_done')}</h3>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2">
                    {t('missions.none_available', { tab: t(`missions.tabs.${activeTab}`) })}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reward Celebration Modal */}
      <AnimatePresence>
        {claimedReward && (
          <div className="absolute inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setClaimedReward(null)}
              className="absolute inset-0 bg-white/80 backdrop-blur-md"
            />
            
            <motion.div
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.5, opacity: 0, y: 50 }}
              className="bg-white w-full max-w-sm rounded-[40px] p-8 border-4 border-cyan-400/30 relative z-10 shadow-2xl flex flex-col items-center text-center"
            >
              <div className="w-24 h-24 bg-cyan-400/20 rounded-full flex items-center justify-center mb-6 relative">
                 <motion.div 
                   animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                   transition={{ repeat: Infinity, duration: 3 }}
                   className="relative z-10"
                 >
                   <Gift className="w-12 h-12 text-cyan-600" />
                 </motion.div>
                 <div className="absolute inset-0 bg-cyan-400/20 rounded-full animate-ping" />
              </div>

              <h3 className="text-3xl font-num uppercase text-slate-900 mb-2 tracking-tight">{t('missions.reward_modal.title')}</h3>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-8">{t('missions.reward_modal.subtitle', { name: claimedReward.name })}</p>
              
              <div className="flex flex-wrap justify-center gap-4 mb-10 w-full">
                {obtainedRewards.map((reward, i) => {
                  const isPrism = reward.type === 'prism';
                  const isBox = reward.type === 'box';
                  
                  return (
                    <motion.div 
                      key={i}
                      initial={{ scale: 0, y: 20 }}
                      animate={{ scale: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className={cn(
                        "bg-slate-50 p-4 rounded-3xl border-b-4 border-slate-200 flex flex-col items-center gap-2 min-w-[120px]",
                        isPrism && "border-purple-200 bg-purple-50/50",
                        isBox && "border-amber-200 bg-amber-50/50"
                      )}
                    >
                      <div className={cn(
                        "w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-1 overflow-hidden",
                      )}>
                        {reward.type === 'coins' && <CurrencyIcon type="coin" size="md" />}
                        {reward.type === 'gems' && <CurrencyIcon type="gem" size="md" />}
                        {reward.type === 'exp' && <CurrencyIcon type="exp" size="md" />}
                        {reward.type === 'tickets' && <CurrencyIcon type="ticket_heart" size="md" />}
                        {reward.type === 'star_ticket' && <CurrencyIcon type="ticket_star" size="md" />}
                        {isBox && (
                          <div className="scale-125">
                            <ChestIcon tier={(reward.subType || 'bronze') as any} size="sm" />
                          </div>
                        )}
                        {isPrism && (
                          <div className="scale-75">
                            <AnimaTicketIcon stars={reward.stars || 1} size="sm" />
                          </div>
                        )}
                        {!['coins', 'gems', 'exp', 'tickets', 'box', 'prism', 'star_ticket'].includes(reward.type) && <Gift className="w-8 h-8 text-cyan-600" />}
                      </div>
                      {isPrism ? (
                        <>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">{t('missions.reward_modal.anima_ticket')}</div>
                          <div className="text-xl font-num font-black text-slate-900">{reward.amount}x</div>
                        </>
                      ) : (
                        <>
                          <div className="text-xl font-num font-black text-slate-900">{reward.amount}</div>
                        </>
                      )}
                    </motion.div>
                  );
                })}
              </div>

              <button
                onClick={() => {
                  audioEngine.playSound('click');
                  setClaimedReward(null);
                  setObtainedRewards([]);
                  if (pendingLevelUps.length > 0) {
                    pendingLevelUps.forEach(res => {
                      onQueueLevelUp(res);
                    });
                    setPendingLevelUps([]);
                  }
                }}
                className="w-full bg-cyan-500 text-white font-num text-xl py-5 rounded-3xl shadow-lg active:translate-y-1 transition-all uppercase tracking-widest toy-3d-button border-none"
              >
                {t('missions.reward_modal.awesome')}
              </button>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {showPremiumModal && (
        <PremiumModal 
          user={user}
          onClose={() => setShowPremiumModal(false)}
          onSetPremiumStatus={onSetPremiumStatus || (async () => {})}
        />
      )}
    </div>
  );
}
